import dayjs from "dayjs";
import type { PensionPolicy } from "../hooks/api/useGetInsurelyFees";
import { companyLabel } from "./insurelyCompany";

const EXPIRY_MONTHS = 6;

// Collected data goes stale after six months. Unknown savedAt is not counted as expired.
export const isPolicyExpired = (policy: PensionPolicy): boolean =>
  !!policy.savedAt &&
  dayjs(policy.savedAt).isBefore(dayjs().subtract(EXPIRY_MONTHS, "month"));

export const expiredPolicyCount = (policies: PensionPolicy[]): number =>
  policies.filter(isPolicyExpired).length;

// Insurely COLLECTION_STATUS values worth acting on. The two data-carrying ones are fetched
// from the API; the terminal failures explain a company missing from the overview. Every
// other value (RUNNING, LOGIN, TWO_FACTOR_PENDING, COLLECTING, USER_INPUT) is still in
// flight and must not be reported as a failure.
export const DATA_STATUSES = ["COMPLETED", "COMPLETED_PARTIAL"];
const EMPTY_STATUS = "COMPLETED_EMPTY";
const FAILED_STATUSES = [
  "FAILED",
  "AUTHENTICATION_TIMEOUT",
  "CUSTOMER_ENROLLMENT_REQUIRED",
  "INCORRECT_CREDENTIALS",
];

// Companies the user tried to collect from that produced no policy.
export interface CollectionOutcomes {
  /** Collected fine, but no pension came out of it. */
  empty: string[];
  /** Collection ended without data (login failed, enrolment needed, provider error). */
  failed: string[];
}

export const collectionOutcomes = (
  statuses: Record<string, string>,
  policies: PensionPolicy[],
): CollectionOutcomes => {
  // Two codes can be the same company (se-seb-bank collapses onto se-seb), so match labels.
  const withPolicies = new Set(
    policies.map((policy) => companyLabel(policy.company)),
  );
  // One row per company across every round: latest terminal status wins, in-flight is ignored.
  const byLabel = new Map<string, { company: string; status: string }>();

  for (const [company, status] of Object.entries(statuses)) {
    const label = companyLabel(company);
    if (withPolicies.has(label)) continue;
    if (
      status === EMPTY_STATUS ||
      DATA_STATUSES.includes(status) ||
      FAILED_STATUSES.includes(status)
    ) {
      byLabel.set(label, { company, status });
    }
  }

  const empty: string[] = [];
  const failed: string[] = [];
  for (const { company, status } of byLabel.values()) {
    (FAILED_STATUSES.includes(status) ? failed : empty).push(company);
  }

  return { empty, failed };
};
